/**
 * localStorage abstraction layer
 * Centralizes all localStorage access for better maintainability and testability
 */

// Storage keys as constants to prevent typos
export const STORAGE_KEYS = {
    // User data
    NAAM: 'naam',
    GSM: 'gsm',
    EMAIL: 'email',
    UDID: 'udid',

    // Address data
    ADRES: 'adres',
    STRAAT: 'straat',
    NR: 'nr',
    GEMEENTE: 'gemeente',
    POSTCODE: 'postcode',
    LAND: 'land',
    PLACEID: 'placeid',
    LATITUDE: 'latitude',
    LONGITUDE: 'longitude',
    GPS_LAT: 'gpslat',
    GPS_LONG: 'gpslong',

    // Permissions & state
    GEOLOCATION: 'geolocation',
    NOTIFICATIONS: 'notifications',
    CONSENT: 'consent',
    ONBOARDING_COMPLETED: 'onboardingCompleted',

    // Call state
    LAST_CALL_TIME: 'lastCallTime',
    SILENT_CALL_ACTIVE: 'silentCallActive',

    // FCM tokens
    TOKEN: 'token',
    FCM_TOKEN: 'fcm_token',
    FCM_TOKEN_TIMESTAMP: 'fcm_token_timestamp',

    // Language
    LANGUAGE: 'language',
};

/**
 * Get a value from localStorage
 * @param {string} key - Storage key
 * @param {any} defaultValue - Default value if key doesn't exist
 * @returns {string|null}
 */
export const getItem = (key, defaultValue = null) => {
    try {
        const value = localStorage.getItem(key);
        return value !== null ? value : defaultValue;
    } catch (error) {
        console.error('[Storage] Error getting item:', key, error);
        return defaultValue;
    }
};

/**
 * Set a value in localStorage
 * @param {string} key - Storage key
 * @param {any} value - Value to store
 */
export const setItem = (key, value) => {
    try {
        localStorage.setItem(key, value);
    } catch (error) {
        console.error('[Storage] Error setting item:', key, error);
    }
};

/**
 * Remove a value from localStorage
 * @param {string} key - Storage key
 */
export const removeItem = (key) => {
    try {
        localStorage.removeItem(key);
    } catch (error) {
        console.error('[Storage] Error removing item:', key, error);
    }
};

/**
 * Get user address data as an object
 * @returns {Object}
 */
export const getAddress = () => ({
    adres: getItem(STORAGE_KEYS.ADRES, ''),
    straat: getItem(STORAGE_KEYS.STRAAT, ''),
    nr: getItem(STORAGE_KEYS.NR, ''),
    gemeente: getItem(STORAGE_KEYS.GEMEENTE, ''),
    postcode: getItem(STORAGE_KEYS.POSTCODE, ''),
    land: getItem(STORAGE_KEYS.LAND, ''),
    placeid: getItem(STORAGE_KEYS.PLACEID, ''),
    latitude: getItem(STORAGE_KEYS.LATITUDE, ''),
    longitude: getItem(STORAGE_KEYS.LONGITUDE, ''),
    gpslat: getItem(STORAGE_KEYS.GPS_LAT, ''),
    gpslong: getItem(STORAGE_KEYS.GPS_LONG, ''),
});

/**
 * Set user address data from an object
 * @param {Object} address
 */
export const setAddress = (address) => {
    if (address.gpslat !== undefined) setItem(STORAGE_KEYS.GPS_LAT, address.gpslat);
    if (address.gpslong !== undefined) setItem(STORAGE_KEYS.GPS_LONG, address.gpslong);
    if (address.adres !== undefined) setItem(STORAGE_KEYS.ADRES, address.adres);
    if (address.latitude !== undefined) setItem(STORAGE_KEYS.LATITUDE, address.latitude);
    if (address.longitude !== undefined) setItem(STORAGE_KEYS.LONGITUDE, address.longitude);
    if (address.straat !== undefined) setItem(STORAGE_KEYS.STRAAT, address.straat);
    if (address.nr !== undefined) setItem(STORAGE_KEYS.NR, address.nr);
    if (address.gemeente !== undefined) setItem(STORAGE_KEYS.GEMEENTE, address.gemeente);
    if (address.postcode !== undefined) setItem(STORAGE_KEYS.POSTCODE, address.postcode);
    if (address.land !== undefined) setItem(STORAGE_KEYS.LAND, address.land);
    if (address.placeid !== undefined) setItem(STORAGE_KEYS.PLACEID, address.placeid);
};

/**
 * Check if the stored address is valid (all required fields present)
 * @returns {boolean}
 */
export const isAddressValid = () => {
    const straat = getItem(STORAGE_KEYS.STRAAT);
    const nr = getItem(STORAGE_KEYS.NR);
    const gemeente = getItem(STORAGE_KEYS.GEMEENTE);
    const postcode = getItem(STORAGE_KEYS.POSTCODE);
    const land = getItem(STORAGE_KEYS.LAND);
    const placeid = getItem(STORAGE_KEYS.PLACEID);

    return !!(straat && straat !== '' &&
              nr && nr !== '' &&
              gemeente && gemeente !== '' &&
              postcode && postcode !== '' &&
              land && land !== '' &&
              placeid && placeid !== '');
};

/**
 * Clear all address data
 */
export const clearAddress = () => {
    removeItem(STORAGE_KEYS.ADRES);
    removeItem(STORAGE_KEYS.STRAAT);
    removeItem(STORAGE_KEYS.NR);
    removeItem(STORAGE_KEYS.GEMEENTE);
    removeItem(STORAGE_KEYS.POSTCODE);
    removeItem(STORAGE_KEYS.LAND);
    removeItem(STORAGE_KEYS.PLACEID);
    removeItem(STORAGE_KEYS.LATITUDE);
    removeItem(STORAGE_KEYS.LONGITUDE);
    removeItem(STORAGE_KEYS.GPS_LAT);
    removeItem(STORAGE_KEYS.GPS_LONG);
};

/**
 * Get user contact data
 * @returns {Object}
 */
export const getUserData = () => ({
    naam: getItem(STORAGE_KEYS.NAAM, ''),
    gsm: getItem(STORAGE_KEYS.GSM, ''),
    email: getItem(STORAGE_KEYS.EMAIL, ''),
    udid: getItem(STORAGE_KEYS.UDID, ''),
});

/**
 * Set user contact data
 * @param {Object} userData
 */
export const setUserData = (userData) => {
    if (userData.naam !== undefined) setItem(STORAGE_KEYS.NAAM, userData.naam);
    if (userData.gsm !== undefined) setItem(STORAGE_KEYS.GSM, userData.gsm);
    if (userData.email !== undefined) setItem(STORAGE_KEYS.EMAIL, userData.email);
    if (userData.udid !== undefined) setItem(STORAGE_KEYS.UDID, userData.udid);
};

/**
 * Get FCM token data
 * @returns {Object}
 */
export const getTokenData = () => ({
    token: getItem(STORAGE_KEYS.TOKEN, ''),
    fcmToken: getItem(STORAGE_KEYS.FCM_TOKEN, ''),
    timestamp: getItem(STORAGE_KEYS.FCM_TOKEN_TIMESTAMP, ''),
});

/**
 * Set FCM token data
 * @param {string} token
 */
export const setTokenData = (token) => {
    setItem(STORAGE_KEYS.TOKEN, token);
    setItem(STORAGE_KEYS.FCM_TOKEN, token);
    setItem(STORAGE_KEYS.FCM_TOKEN_TIMESTAMP, Date.now().toString());
};

/**
 * Clear FCM token data
 */
export const clearTokenData = () => {
    removeItem(STORAGE_KEYS.TOKEN);
    removeItem(STORAGE_KEYS.FCM_TOKEN);
    removeItem(STORAGE_KEYS.FCM_TOKEN_TIMESTAMP);
};

/**
 * Check if silent call is active
 * @returns {boolean}
 */
export const isSilentCallActive = () => {
    return getItem(STORAGE_KEYS.SILENT_CALL_ACTIVE) === '1';
};

/**
 * Set silent call active state
 * @param {boolean} active
 */
export const setSilentCallActive = (active) => {
    setItem(STORAGE_KEYS.SILENT_CALL_ACTIVE, active ? '1' : '0');
};

/**
 * Check if a call was placed within the last hour
 * @returns {boolean}
 */
export const isCallActive = () => {
    const lastCallTime = parseInt(getItem(STORAGE_KEYS.LAST_CALL_TIME, '0'), 10);
    const now = Date.now();
    return lastCallTime > now - (1000 * 60 * 60); // 1 hour
};

/**
 * Record that a call was placed
 */
export const recordCall = () => {
    setItem(STORAGE_KEYS.LAST_CALL_TIME, Date.now().toString());
};

/**
 * Get geolocation permission state
 * @returns {string} 'granted', 'denied', or ''
 */
export const getGeolocationState = () => {
    return getItem(STORAGE_KEYS.GEOLOCATION, '');
};

/**
 * Set geolocation permission state
 * @param {string} state - 'granted' or 'denied'
 */
export const setGeolocationState = (state) => {
    setItem(STORAGE_KEYS.GEOLOCATION, state);
};

/**
 * Get current language
 * @param {string} defaultLang - Default language code
 * @returns {string}
 */
export const getLanguage = (defaultLang = 'nl') => {
    return getItem(STORAGE_KEYS.LANGUAGE, defaultLang);
};

/**
 * Set current language
 * @param {string} lang - Language code
 */
export const setLanguage = (lang) => {
    setItem(STORAGE_KEYS.LANGUAGE, lang);
};

/**
 * Check if onboarding is completed
 * @returns {boolean}
 */
export const isOnboardingCompleted = () => {
    return getItem(STORAGE_KEYS.ONBOARDING_COMPLETED) === 'true';
};

/**
 * Mark onboarding as completed
 */
export const completeOnboarding = () => {
    setItem(STORAGE_KEYS.ONBOARDING_COMPLETED, 'true');
    setItem(STORAGE_KEYS.CONSENT, 'true');
};

const storageUtils = {
    STORAGE_KEYS,
    getItem,
    setItem,
    removeItem,
    getAddress,
    setAddress,
    isAddressValid,
    clearAddress,
    getUserData,
    setUserData,
    getTokenData,
    setTokenData,
    clearTokenData,
    isSilentCallActive,
    setSilentCallActive,
    isCallActive,
    recordCall,
    getGeolocationState,
    setGeolocationState,
    getLanguage,
    setLanguage,
    isOnboardingCompleted,
    completeOnboarding,
};

export default storageUtils;
